import { z } from 'zod'

export enum GoalStatus {
  ACTIVE = 'active',
  STOPPED = 'stopped',
  DELETED = 'deleted',
}

export enum GoalType {
  LEAD_GEN = 'lead_gen',
  GROW_FOLLOWERS = 'grow_followers',
  LEAD_QUAL = 'lead_qualification',
}

const goalBaseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(GoalStatus),
  type: z.enum(GoalType),
  priority: z.number().optional(),
  ts_updated: z.number(),
})

const goalStatsSchema = z.object({
  stats: z
    .object({
      started: z.number(),
      achieved: z.number(),
    })
    .optional(),
})

const leadGenGoalSchema = goalBaseSchema.extend({
  type: z.literal(GoalType.LEAD_GEN),
  data: z.object({
    context: z.string(),
    message: z.object({
      text: z.string(),
      button: z.object({
        title: z.string(),
        url: z.string(),
      }),
    }),
  }),
})

const fallbackAssigneeSchema = z.object({
  group_id: z.number().nullable(),
  user_id: z.number().nullable(),
})

export enum ExitActionType {
  SEND_MESSAGE = 'send_message',
  START_FLOW = 'start_flow',
  ASSIGN_CONVERSATION = 'assign_conversation',
}

const sendMessageActionSchema = z.object({
  type: z.literal(ExitActionType.SEND_MESSAGE),
  data: z.object({
    message: z.object({
      text: z.string(),
      button: z
        .object({
          title: z.string(),
          url: z.string(),
        })
        .optional(),
    }),
  }),
})

const startFlowActionSchema = z.object({
  type: z.literal(ExitActionType.START_FLOW),
  data: z.object({
    flow_ns: z.string(),
  }),
})

const assignConversationActionSchema = z.object({
  type: z.literal(ExitActionType.ASSIGN_CONVERSATION),
  data: z.object({
    assignee: fallbackAssigneeSchema,
  }),
})

const exitActionSchema = z.discriminatedUnion('type', [
  sendMessageActionSchema,
  startFlowActionSchema,
  assignConversationActionSchema,
])

const leadQualGoalSchema = goalBaseSchema.extend({
  type: z.literal(GoalType.LEAD_QUAL),
  data: z.object({
    fields: z.array(z.string()),
    settings: z.object({
      save_phone_as_whatsapp_id: z.boolean(),
      fallback_assignee: fallbackAssigneeSchema,
      exit_action: exitActionSchema.nullable(),
    }),
    context: z.string(),
  }),
})

const growFollowersGoalSchema = goalBaseSchema.extend({
  type: z.literal(GoalType.GROW_FOLLOWERS),
  data: z.object({
    message: z.object({
      text: z.string(),
    }),
  }),
})

const goalSchema = z.discriminatedUnion('type', [
  leadGenGoalSchema,
  growFollowersGoalSchema,
  leadQualGoalSchema,
])

const goalExtendedSchema = z.discriminatedUnion('type', [
  leadGenGoalSchema.merge(goalStatsSchema),
  growFollowersGoalSchema.merge(goalStatsSchema),
  leadQualGoalSchema.merge(goalStatsSchema),
])

const generateLeadGenGoalSchema = z.object({
  goal_id: z.string().uuid(),
  context: z.string(),
  url: z.string(),
})

const fieldsStatsSchema = z.object({
  data: z.object({
    id: z.union([z.string(), z.number()]),
  }),
  stats: z.object({
    saved: z.number(),
  }),
})

const leadQualStatsSchema = z.object({
  sufs: z.array(fieldsStatsSchema),
  cufs: z.array(fieldsStatsSchema),
})

export const AiEngagementGoalApiSchemas = {
  list: {
    response: z.object({
      goals: z.array(goalExtendedSchema),
    }),
    query: z
      .object({
        status: z.enum(GoalStatus).optional(),
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  create: {
    request: z.object({
      goal: goalSchema,
    }),
    response: z.object({
      goal: goalSchema,
    }),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  update: {
    request: z.object({
      goal: goalSchema,
    }),
    response: z.object({
      goal: goalSchema,
    }),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  updatePriority: {
    request: z.object({
      priority: z.array(z.string().uuid()),
    }),
    response: z.undefined(),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  generateLeadGenMessage: {
    request: generateLeadGenGoalSchema,
    response: z.undefined(),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  getStats: {
    response: leadQualStatsSchema,
    query: z.object({
      goal_id: z.string(),
    }),
    path: z.undefined(),
  },
}

export type EngagementGoal = z.infer<typeof goalSchema>
export type EngagementGoalExtended = z.infer<typeof goalExtendedSchema>
export type EngagementGoalStats = NonNullable<z.infer<typeof goalStatsSchema>['stats']>
export type LeadGenGoal = z.infer<typeof leadGenGoalSchema>
export type GrowFollowersGoal = z.infer<typeof growFollowersGoalSchema>
export type LeadQualGoal = z.infer<typeof leadQualGoalSchema>
export type LeadQualFallbackAssignee = z.infer<typeof fallbackAssigneeSchema>
export type LeadQualExitAction = z.infer<typeof exitActionSchema>
export type LeadQualStats = z.infer<typeof leadQualStatsSchema>
export type FieldStats = z.infer<typeof fieldsStatsSchema>

export const AiEngagementGoalSchemas = {
  goal: goalSchema,
}
